import React, { useEffect, useState } from "react";
import { Printer, TrendUp, TrendDown, Money } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatIDR, formatDate } from "../lib/api";
import { generatePDF } from "../lib/pdf";

export default function Cashflow() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState([]);
  const [saldo, setSaldo] = useState(null);

  useEffect(() => {
    (async () => {
      const [cf, sd] = await Promise.all([api.get(`/reports/cashflow?days=${days}`), api.get("/reports/saldo")]);
      setData(cf.data); setSaldo(sd.data);
    })();
  }, [days]);

  const income = data.reduce((a, b) => a + (b.income || 0), 0);
  const expense = data.reduce((a, b) => a + (b.expense || 0), 0);

  const printPDF = () => {
    generatePDF({
      title: `Laporan Cashflow ${days} Hari Terakhir`,
      subtitle: "EduFoundation",
      headers: ["Tanggal", "Pemasukan", "Pengeluaran", "Net"],
      rows: data.map((d) => [formatDate(d.date), formatIDR(d.income), formatIDR(d.expense), formatIDR(d.income - d.expense)]),
      meta: { "Total Pemasukan": formatIDR(income), "Total Pengeluaran": formatIDR(expense), "Net": formatIDR(income - expense), "Saldo Real (all-time)": formatIDR(saldo?.saldo || 0) },
      filename: `cashflow-${days}d-${Date.now()}.pdf`,
    });
  };

  return (
    <div data-testid="cashflow-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Analitik Keuangan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Cashflow & Saldo Real</h1>
          <p className="text-gray-600 text-sm mt-1">Ringkasan arus kas dan saldo yayasan.</p>
        </div>
        <div className="flex gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="7">7 Hari</SelectItem>
              <SelectItem value="14">14 Hari</SelectItem>
              <SelectItem value="30">30 Hari</SelectItem>
              <SelectItem value="90">90 Hari</SelectItem>
              <SelectItem value="365">1 Tahun</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={printPDF} data-testid="cashflow-print-button"><Printer size={16} className="mr-1.5" />Cetak PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-md p-5 card-lift">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Saldo Real</div>
            <Money size={16} className="text-[#0B8D95]" />
          </div>
          <div className={`font-mono text-2xl font-bold ${saldo?.saldo >= 0 ? "text-[#0B8D95]" : "text-[#DC2626]"}`}>{formatIDR(saldo?.saldo || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">Total Pemasukan − Total Pengeluaran</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5 card-lift">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Pemasukan ({days}d)</div>
            <TrendUp size={16} className="text-[#059669]" />
          </div>
          <div className="font-mono text-2xl font-bold text-[#059669]">{formatIDR(income)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5 card-lift">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Pengeluaran ({days}d)</div>
            <TrendDown size={16} className="text-[#DC2626]" />
          </div>
          <div className="font-mono text-2xl font-bold text-[#DC2626]">{formatIDR(expense)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5 card-lift">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Net ({days}d)</div>
          </div>
          <div className={`font-mono text-2xl font-bold ${(income - expense) >= 0 ? "text-gray-900" : "text-[#DC2626]"}`}>{formatIDR(income - expense)}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="font-display text-lg font-bold text-gray-900 mb-4">Arus Kas Harian</div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={0.3}/><stop offset="95%" stopColor="#059669" stopOpacity={0}/></linearGradient>
                <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#DC2626" stopOpacity={0.3}/><stop offset="95%" stopColor="#DC2626" stopOpacity={0}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d)=>d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip formatter={(v)=>formatIDR(v)} />
              <Area type="monotone" dataKey="income" stroke="#059669" fillOpacity={1} fill="url(#colorIncome)" strokeWidth={2} />
              <Area type="monotone" dataKey="expense" stroke="#DC2626" fillOpacity={1} fill="url(#colorExpense)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 font-display text-lg font-bold text-gray-900">Saldo per Nomenklatur</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-5 py-3 font-semibold">Kode</th><th className="px-5 py-3 font-semibold">Nama</th><th className="px-5 py-3 font-semibold text-right">Pemasukan</th><th className="px-5 py-3 font-semibold text-right">Pengeluaran</th><th className="px-5 py-3 font-semibold text-right">Saldo</th></tr>
            </thead>
            <tbody>
              {saldo?.per_nomenklatur?.map((n) => (
                <tr key={n.code} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono font-semibold text-[#0B8D95]">{n.code}</td>
                  <td className="px-5 py-3">{n.name}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#059669]">{formatIDR(n.income)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#DC2626]">{formatIDR(n.expense)}</td>
                  <td className={`px-5 py-3 text-right font-mono font-bold ${n.balance >= 0 ? "text-gray-900" : "text-[#DC2626]"}`}>{formatIDR(n.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
